import { Button } from "@scaffquote/ui";
import { DatabaseZap, RefreshCw, RotateCw, TriangleAlert } from "lucide-react";
import { Outlet } from "react-router";
import { retryFieldStorage, useFieldStorage } from "./lib/storage-status.ts";

/**
 * Wraps every /field screen in the high-contrast "sun" theme: black on white,
 * yellow active highlight, 18 px minimum text and 64 px targets (see
 * packages/ui/src/styles.css). Components pick the theme up from CSS
 * variables, so field screens use the same components as the office.
 *
 * Storage state shows on every field screen (Lap and Review included), so
 * the outbox never quietly becomes non-persistent: a storage problem blocks
 * capture, and a tab-only store says so above whatever screen is open.
 */
export function FieldLayout() {
  const problem = useFieldStorage((s) => s.problem);
  const durable = useFieldStorage((s) => s.durable);
  return (
    <div data-theme="sun" className="-mx-4 min-h-[calc(100dvh-7rem)] px-4 py-2 lg:-mx-8 lg:px-8">
      {problem ? (
        <StorageProblem detail={problem} />
      ) : (
        <>
          {!durable ? <NotDurableBanner /> : null}
          <Outlet />
        </>
      )}
    </div>
  );
}

export const STORAGE_PROBLEM_TITLE = "Storage problem – readings can't be saved offline";

function StorageProblem({ detail }: { detail: string }) {
  return (
    <section
      role="alert"
      aria-labelledby="storage-problem-title"
      className="flex flex-col gap-4 rounded-md border-4 border-black p-4"
    >
      <h1 id="storage-problem-title" className="flex items-start gap-2 font-bold text-2xl">
        <DatabaseZap aria-hidden className="mt-1 size-7 shrink-0" />
        {STORAGE_PROBLEM_TITLE}
      </h1>
      <p>
        This device's storage didn't open, so the app won't take readings that a reload could lose.
        Readings already saved on this device stay where they are.
      </p>
      <p>Try again. If it keeps happening, reload the app, or close it fully and open it again.</p>
      <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-2">
        <Button
          size="lg"
          icon={<RotateCw aria-hidden className="size-5" />}
          onClick={retryFieldStorage}
        >
          Try again
        </Button>
        <Button
          size="lg"
          variant="secondary"
          icon={<RefreshCw aria-hidden className="size-5" />}
          onClick={() => window.location.reload()}
        >
          Reload the app
        </Button>
      </div>
      <p className="break-words text-base">Details for the office: {detail}</p>
    </section>
  );
}

function NotDurableBanner() {
  return (
    <p
      role="alert"
      className="mb-3 flex items-start gap-2 rounded-md border-2 border-black bg-active p-3 font-semibold text-on-active"
    >
      <TriangleAlert aria-hidden className="mt-1 size-5 shrink-0" />
      <span>
        Not saved on this device: this browser won't store work offline, so a reload loses unsynced
        readings. Sync before you close the app, or install it to your Home Screen.
      </span>
    </p>
  );
}
